import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import {
  useDeleteRecipe,
  useGetDemoKitUsage,
  useGetRecipeList,
  useUpdateRecipeType
} from "../../../../api/recipeSimulator";
import { RecipeTypeClassification } from "../../../../config/enums/recipeSimulator.enum";
import { errorView } from "../../../../helpers/ErrorHandler";
import { useAuthContext } from "../../../../hooks/useAuthContext";
import { isAdminAuthorized } from "../../../../utils/utils";
import { getCurrentUserId, getRecipeRowActions } from "../recipeList.permissions";
import {
  isDemoRecipe,
  isSuccessfullyCalculated,
  mapRecipesToGroupRecords,
  sortRecipeGroupRecords,
  toRecipeRecord
} from "../recipeList.utils";
import { RecipeGroupRecord, RecipeRowActionType, SortField } from "../types";

const PAGE_LIMIT = 10;
const SEARCH_DEBOUNCE_MS = 400;

const handleMutationError = (error: unknown) => {
  errorView(error);
};

export const useRecipeSimulatorList = () => {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const isAdmin = isAdminAuthorized(user);
  const currentUserId = getCurrentUserId(user);
  const actionContext = useMemo(() => ({ isAdmin, currentUserId }), [isAdmin, currentUserId]);

  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortAsc, setSortAsc] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [newRecipeOpen, setNewRecipeOpen] = useState(false);
  const [usageOpen, setUsageOpen] = useState(false);
  const [usageDemoKitId, setUsageDemoKitId] = useState<string | null>(null);
  const [usageTargetName, setUsageTargetName] = useState("");
  const [demoConfirmOpen, setDemoConfirmOpen] = useState(false);
  const [demoConfirmTarget, setDemoConfirmTarget] = useState<RecipeGroupRecord | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteConfirmTarget, setDeleteConfirmTarget] = useState<RecipeGroupRecord | null>(null);
  const [publicConfirmOpen, setPublicConfirmOpen] = useState(false);
  const [publicConfirmTarget, setPublicConfirmTarget] = useState<RecipeGroupRecord | null>(null);
  const [privateConfirmOpen, setPrivateConfirmOpen] = useState(false);
  const [privateConfirmTarget, setPrivateConfirmTarget] = useState<RecipeGroupRecord | null>(null);
  const [pendingRowAction, setPendingRowAction] = useState<{
    recipeId: string;
    action: RecipeRowActionType;
  } | null>(null);

  const {
    data: recipeSimulatorList,
    isLoading: isRecipeListLoading,
    isFetching: isRecipeListFetching,
    refetch: refetchRecipeList
  } = useGetRecipeList(pageNumber, PAGE_LIMIT, debouncedSearch);

  const isInitialTableLoading = isRecipeListLoading && !recipeSimulatorList;
  const isTableRefetching = isRecipeListFetching && !isInitialTableLoading;
  const isSearchPending = searchInput !== debouncedSearch;

  const { isLoading: isRecipeDeleteLoading, mutate: deleteRecipe } = useDeleteRecipe({
    onError: handleMutationError
  });
  const { isLoading: isRecipeTypeUpdateLoading, mutate: updateRecipeType } = useUpdateRecipeType({
    onError: handleMutationError
  });
  const { data: usageRecords = [], isLoading: isUsageLoading } = useGetDemoKitUsage(
    usageDemoKitId,
    usageOpen
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPageNumber(1);
  }, [debouncedSearch]);

  const sortedRecords = useMemo(() => {
    const records = mapRecipesToGroupRecords(recipeSimulatorList?.result);
    if (!sortField) {
      return records;
    }
    return sortRecipeGroupRecords(records, sortField, sortAsc);
  }, [recipeSimulatorList?.result, sortAsc, sortField]);

  const totalPages = recipeSimulatorList?.total
    ? Math.ceil(recipeSimulatorList.total / PAGE_LIMIT)
    : 1;

  const totalCount = recipeSimulatorList?.total ?? sortedRecords.length;

  const clearPendingRowAction = useCallback(() => {
    setPendingRowAction(null);
  }, []);

  const startPendingRowAction = useCallback((recipeId: string, action: RecipeRowActionType) => {
    setPendingRowAction({ recipeId, action });
  }, []);

  const isActionLoading = useCallback(
    (recipeId: string, action: RecipeRowActionType) => {
      if (action === "usage") {
        return usageOpen && usageDemoKitId === recipeId && isUsageLoading;
      }

      if (pendingRowAction?.recipeId !== recipeId || pendingRowAction.action !== action) {
        return false;
      }

      if (action === "delete") {
        return isRecipeDeleteLoading;
      }

      if (action === "demo" || action === "public" || action === "private") {
        return isRecipeTypeUpdateLoading;
      }

      return false;
    },
    [
      isRecipeDeleteLoading,
      isRecipeTypeUpdateLoading,
      isUsageLoading,
      pendingRowAction,
      usageDemoKitId,
      usageOpen
    ]
  );

  const isRowBusy = useCallback(
    (recipeId: string) =>
      (pendingRowAction?.recipeId === recipeId &&
        (isRecipeDeleteLoading || isRecipeTypeUpdateLoading)) ||
      (usageOpen && usageDemoKitId === recipeId && isUsageLoading),
    [
      isRecipeDeleteLoading,
      isRecipeTypeUpdateLoading,
      isUsageLoading,
      pendingRowAction,
      usageDemoKitId,
      usageOpen
    ]
  );

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortAsc(current => !current);
      return;
    }
    setSortField(field);
    setSortAsc(true);
  };

  const navigateToManage = (
    recipe: RecipeGroupRecord,
    options: { viewOnly?: boolean; scrollToResults?: boolean; variantId?: string } = {}
  ) => {
    const viewOnly = options.viewOnly ?? false;
    navigate(`/recipe-simulator/${recipe.id}/manage`, {
      state: {
        viewOnly,
        scrollToResults: viewOnly || Boolean(options.scrollToResults),
        variantId: options.variantId
      }
    });
  };

  const handleDelete = (event: React.MouseEvent, recipe: RecipeGroupRecord) => {
    event.stopPropagation();
    const actions = getRecipeRowActions(toRecipeRecord(recipe), actionContext);
    if (!actions.canDelete) {
      return;
    }

    setDeleteConfirmTarget(recipe);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirmOpenChange = (open: boolean) => {
    setDeleteConfirmOpen(open);
    if (!open) {
      setDeleteConfirmTarget(null);
    }
  };

  const confirmDelete = () => {
    if (!deleteConfirmTarget) {
      return;
    }

    startPendingRowAction(deleteConfirmTarget.id, "delete");
    deleteRecipe(deleteConfirmTarget.id, {
      onSuccess: () => {
        refetchRecipeList();
        setDeleteConfirmOpen(false);
        setDeleteConfirmTarget(null);
      },
      onSettled: clearPendingRowAction
    });
  };

  const handleToggleDemo = (event: React.MouseEvent, recipe: RecipeGroupRecord) => {
    event.stopPropagation();
    const recipeRecord = toRecipeRecord(recipe);
    const actions = getRecipeRowActions(recipeRecord, actionContext);

    if (!actions.canToggleDemoKit || actions.isDemoKitToggleDisabled) {
      if (isAdmin && !isDemoRecipe(recipeRecord) && !isSuccessfullyCalculated(recipeRecord)) {
        toast.error("Recipe must be successfully calculated before converting to a demo kit.", {
          style: { background: "#fff" }
        });
      }
      return;
    }

    if (!isDemoRecipe(recipeRecord)) {
      setDemoConfirmTarget(recipe);
      setDemoConfirmOpen(true);
      return;
    }

    startPendingRowAction(recipe.id, "demo");
    // If admin is removing another user's demokit, set to PUBLIC;
    // if owner is removing their own, set to PRIVATE
    const recipeCreatedById = String(recipe.createdById || '');
    const isOwnRecipe = recipeCreatedById === String(currentUserId || '');
    const isAdminRemovingOtherUserDemoKit = isAdmin && !isOwnRecipe;
    const newClassification = isAdminRemovingOtherUserDemoKit
      ? RecipeTypeClassification.PUBLIC
      : RecipeTypeClassification.PRIVATE;

    updateRecipeType(
      {
        recipeId: recipe.id,
        recipeTypeClassification: newClassification
      },
      {
        onSuccess: () => {
          toast.success(`"${recipe.recipeName}" is no longer a demo kit.`, {
            style: { background: "#fff" }
          });
          refetchRecipeList();
        },
        onSettled: clearPendingRowAction
      }
    );
  };

  const confirmMakeDemo = () => {
    if (!demoConfirmTarget) {
      return;
    }

    startPendingRowAction(demoConfirmTarget.id, "demo");
    updateRecipeType(
      {
        recipeId: demoConfirmTarget.id,
        recipeTypeClassification: RecipeTypeClassification.DEMOKIT
      },
      {
        onSuccess: () => {
          toast.success(`"${demoConfirmTarget.recipeName}" has been marked as a demo kit.`, {
            style: { background: "#fff" }
          });
          refetchRecipeList();
          setDemoConfirmOpen(false);
          setDemoConfirmTarget(null);
        },
        onSettled: clearPendingRowAction
      }
    );
  };

  const handleTogglePublic = (event: React.MouseEvent, recipe: RecipeGroupRecord) => {
    event.stopPropagation();
    const actions = getRecipeRowActions(toRecipeRecord(recipe), actionContext);

    if (actions.canMakePrivate) {
      setPrivateConfirmTarget(recipe);
      setPrivateConfirmOpen(true);
      return;
    }

    if (actions.canMakePublic) {
      setPublicConfirmTarget(recipe);
      setPublicConfirmOpen(true);
    }
  };

  const handlePrivateConfirmOpenChange = (open: boolean) => {
    setPrivateConfirmOpen(open);
    if (!open) {
      setPrivateConfirmTarget(null);
    }
  };

  const confirmMakePrivate = () => {
    if (!privateConfirmTarget) {
      return;
    }

    startPendingRowAction(privateConfirmTarget.id, "private");
    updateRecipeType(
      {
        recipeId: privateConfirmTarget.id,
        recipeTypeClassification: RecipeTypeClassification.PRIVATE
      },
      {
        onSuccess: () => {
          toast.success(`"${privateConfirmTarget.recipeName}" is now private.`, {
            style: { background: "#fff" }
          });
          refetchRecipeList();
          setPrivateConfirmOpen(false);
          setPrivateConfirmTarget(null);
        },
        onSettled: clearPendingRowAction
      }
    );
  };

  const confirmMakePublic = () => {
    if (!publicConfirmTarget) {
      return;
    }

    startPendingRowAction(publicConfirmTarget.id, "public");
    updateRecipeType(
      {
        recipeId: publicConfirmTarget.id,
        recipeTypeClassification: RecipeTypeClassification.PUBLIC
      },
      {
        onSuccess: () => {
          toast.success(
            `"${publicConfirmTarget.recipeName}" is now public and viewable by anyone.`,
            {
              style: { background: "#fff" }
            }
          );
          refetchRecipeList();
          setPublicConfirmOpen(false);
          setPublicConfirmTarget(null);
        },
        onSettled: clearPendingRowAction
      }
    );
  };

  const handleUsage = (event: React.MouseEvent, recipe: RecipeGroupRecord) => {
    event.stopPropagation();
    const recipeRecord = toRecipeRecord(recipe);
    const actions = getRecipeRowActions(recipeRecord, actionContext);
    if (!actions.canViewDemoKitUsage) {
      return;
    }

    setUsageTargetName(`${recipe.recipeName}`);
    setUsageDemoKitId(recipe.id);
    setUsageOpen(true);
  };

  const handleUsageOpenChange = (open: boolean) => {
    setUsageOpen(open);
    if (!open) {
      setUsageDemoKitId(null);
      setUsageTargetName("");
    }
  };

  const rowHandlers = {
    onRowClick: (recipe: RecipeGroupRecord) => {
      const actions = getRecipeRowActions(toRecipeRecord(recipe), actionContext);
      if (actions.isViewOnly) {
        navigateToManage(recipe, { viewOnly: true });
        return;
      }

      navigateToManage(recipe);
    },
    onView: (event: React.MouseEvent, recipe: RecipeGroupRecord) => {
      event.stopPropagation();
      const actions = getRecipeRowActions(toRecipeRecord(recipe), actionContext);
      if (!actions.canView) {
        return;
      }

      navigateToManage(recipe, { viewOnly: true });
    },
    onEdit: (event: React.MouseEvent, recipe: RecipeGroupRecord) => {
      event.stopPropagation();
      const actions = getRecipeRowActions(toRecipeRecord(recipe), actionContext);
      if (!actions.canEdit) {
        return;
      }

      navigateToManage(recipe, { viewOnly: false });
    },
    onDelete: handleDelete,
    onUsage: handleUsage,
    onToggleDemo: handleToggleDemo,
    onTogglePublic: handleTogglePublic,
    isActionLoading,
    isRowBusy
  };

  return {
    isAdmin,
    currentUserId,
    search: searchInput,
    setSearch: setSearchInput,
    isSearchPending,
    sortField,
    sortAsc,
    toggleSort,
    pageNumber,
    setPageNumber,
    totalPages,
    totalCount,
    sortedRecords,
    isInitialTableLoading,
    isTableRefetching,
    isRecipeListLoading,
    isRecipeDeleteLoading,
    isRecipeTypeUpdateLoading,
    newRecipeOpen,
    setNewRecipeOpen,
    usageOpen,
    setUsageOpen: handleUsageOpenChange,
    usageTargetName,
    usageRecords,
    isUsageLoading,
    demoConfirmOpen,
    setDemoConfirmOpen,
    deleteConfirmOpen,
    setDeleteConfirmOpen: handleDeleteConfirmOpenChange,
    deleteConfirmTarget,
    publicConfirmOpen,
    setPublicConfirmOpen,
    privateConfirmOpen,
    setPrivateConfirmOpen: handlePrivateConfirmOpenChange,
    privateConfirmTarget,
    confirmDelete,
    confirmMakeDemo,
    confirmMakePublic,
    confirmMakePrivate,
    refetchRecipeList,
    rowHandlers
  };
};
